import { pgTable, text, boolean, timestamp, integer, bigint, index, uniqueIndex, foreignKey, jsonb } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { membershipStatus, telegramChatType, userBotLinkStatus } from './enums';
import { user } from './user';
import { apiKey } from './auxiliary';

export const telegramChannel = pgTable('TelegramChannel', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  telegramId: bigint({ mode: 'number' }).notNull(),
  title: text().notNull(),
  description: text(),
  username: text(),
  memberCount: integer().default(0).notNull(),
  photoUrl: text(),
  type: telegramChatType().default('channel').notNull(),
  lastSyncedAt: timestamp({ precision: 3, mode: 'date' }),
  isActive: boolean().default(true).notNull(),
  addedByBotId: integer(),
  /** Invite link для приватных чатов/каналов */
  inviteLink: text(),
  /** Join Request ссылка (creates_join_request: true) для контроля доступа */
  joinRequestLink: text(),
  /** Может ли бот отправлять сообщения (права администратора) */
  canSendMessages: boolean().default(false).notNull(),
  /** Может ли бот приглашать пользователей */
  canInviteUsers: boolean().default(false).notNull(),
  /** Может ли бот удалять пользователей */
  canRestrictMembers: boolean().default(false).notNull(),
  /** Статус бота в чате: administrator, member, left, kicked */
  botStatus: text().default('member'),
  /** Дата когда бот был добавлен в чат */
  botAddedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('TelegramChannel_isActive_idx').using('btree', table.isActive.asc().nullsLast()),
  uniqueIndex('TelegramChannel_telegramId_key').using('btree', table.telegramId.asc().nullsLast()),
  index('TelegramChannel_addedByBotId_idx').using('btree', table.addedByBotId.asc().nullsLast()),
  index('TelegramChannel_type_idx').using('btree', table.type.asc().nullsLast()),
]);

export const telegramBot = pgTable('TelegramBot', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  name: text().notNull(),
  apiKeyId: integer().notNull(),
  webhookUrl: text(),
  webhookEnabled: boolean().default(false).notNull(),
  webhookSecret: text(),
  isPrimary: boolean().default(false).notNull(),
  username: text(),
  avatarUrl: text(),
  description: text(),
  shortDescription: text(),
  capabilities: jsonb(),
  statistics: jsonb(),
  adminChannels: jsonb(),
  /**
   * Список ID разрешённых стран для регистрации в этом боте.
   * Пустой массив = все активные страны (поведение по умолчанию).
   * Если ровно один ID — шаг выбора страны в онбординге пропускается, юзеру ставится этот country.
   */
  allowedCountryIds: integer().array().notNull().default(sql`ARRAY[]::integer[]`),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  uniqueIndex('TelegramBot_apiKeyId_key').using('btree', table.apiKeyId.asc().nullsLast()),
  index('TelegramBot_isPrimary_idx').using('btree', table.isPrimary.asc().nullsLast()),
  uniqueIndex('TelegramBot_name_key').using('btree', table.name.asc().nullsLast()),
  uniqueIndex('TelegramBot_webhookSecret_key').using('btree', table.webhookSecret.asc().nullsLast()),
  foreignKey({
    columns: [table.apiKeyId],
    foreignColumns: [apiKey.id],
    name: 'TelegramBot_apiKeyId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);


export const channelMembership = pgTable('ChannelMembership', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(),
  channelId: integer().notNull(),
  status: membershipStatus().default('PENDING').notNull(),
  joinedAt: timestamp({ precision: 3, mode: 'date' }),
  leftAt: timestamp({ precision: 3, mode: 'date' }),
  reason: text(),
  inviteLink: text(),

  /** Связь с AccessGrant - право доступа, на основе которого создано членство */
  accessGrantId: integer(),

  /** Последняя ошибка при операции (invite/kick) */
  lastError: text(),
  /** Время последней ошибки */
  lastErrorAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('ChannelMembership_channelId_idx').using('btree', table.channelId.asc().nullsLast()),
  index('ChannelMembership_status_idx').using('btree', table.status.asc().nullsLast()),
  uniqueIndex('ChannelMembership_userId_channelId_key').using('btree', table.userId.asc().nullsLast(), table.channelId.asc().nullsLast()),
  index('ChannelMembership_userId_idx').using('btree', table.userId.asc().nullsLast()),
  index('ChannelMembership_accessGrantId_idx').using('btree', table.accessGrantId.asc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'ChannelMembership_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.channelId],
    foreignColumns: [telegramChannel.id],
    name: 'ChannelMembership_channelId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  // Note: FK to AccessGrant added in relations.ts to avoid circular dependency
]);

export const channelInviteLink = pgTable('ChannelInviteLink', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  channelId: integer().notNull(),
  inviteLink: text().notNull(),
  expiresAt: timestamp({ precision: 3, mode: 'date' }),
  memberLimit: integer(),
  isRevoked: boolean().default(false).notNull(),
  createdByUserId: integer(),
  createdByAdminId: integer(),
  usedByUserId: integer(),
  usedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  index('ChannelInviteLink_channelId_idx').using('btree', table.channelId.asc().nullsLast()),
  index('ChannelInviteLink_isRevoked_idx').using('btree', table.isRevoked.asc().nullsLast()),
  foreignKey({
    columns: [table.channelId],
    foreignColumns: [telegramChannel.id],
    name: 'ChannelInviteLink_channelId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);

export const channelAnalytics = pgTable('ChannelAnalytics', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  channelId: integer().notNull(),
  memberCount: integer().notNull(),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
}, (table) => [
  index('ChannelAnalytics_channelId_createdAt_idx').using('btree', table.channelId.asc().nullsLast(), table.createdAt.asc().nullsLast()),
  foreignKey({
    columns: [table.channelId],
    foreignColumns: [telegramChannel.id],
    name: 'ChannelAnalytics_channelId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);

/**
 * Связь пользователь ↔ Telegram-бот: трекинг того, что юзер пользуется конкретным
 * ботом, и статус (ACTIVE — общается, BLOCKED — заблокировал бота).
 * Уникальность по (userId, botId): одна запись на пару юзер-бот.
 */
export const userBotLink = pgTable('UserBotLink', {
  id: integer().primaryKey().generatedAlwaysAsIdentity(),
  userId: integer().notNull(),
  botId: integer().notNull(),
  status: userBotLinkStatus().default('ACTIVE').notNull(),
  /** Когда юзер впервые провзаимодействовал с ботом */
  firstSeenAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  /** Последнее взаимодействие юзера с ботом */
  lastSeenAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  /** Когда юзер заблокировал бота (status=BLOCKED) */
  blockedAt: timestamp({ precision: 3, mode: 'date' }),
  /** Когда юзер разблокировал бота (вернулся в ACTIVE) */
  unblockedAt: timestamp({ precision: 3, mode: 'date' }),
  createdAt: timestamp({ precision: 3, mode: 'date' }).default(sql`CURRENT_TIMESTAMP`).notNull(),
  updatedAt: timestamp({ precision: 3, mode: 'date' }).notNull(),
}, (table) => [
  uniqueIndex('UserBotLink_userId_botId_key').using('btree', table.userId.asc().nullsLast(), table.botId.asc().nullsLast()),
  index('UserBotLink_botId_idx').using('btree', table.botId.asc().nullsLast()),
  index('UserBotLink_status_idx').using('btree', table.status.asc().nullsLast()),
  index('UserBotLink_userId_idx').using('btree', table.userId.asc().nullsLast()),
  foreignKey({
    columns: [table.userId],
    foreignColumns: [user.id],
    name: 'UserBotLink_userId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
  foreignKey({
    columns: [table.botId],
    foreignColumns: [telegramBot.id],
    name: 'UserBotLink_botId_fkey'
  }).onUpdate('cascade').onDelete('cascade'),
]);
